"use client";

import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, X } from "lucide-react";
import debounce from "lodash.debounce";
import { useStorageQuery } from "../../../hooks/storageQueryProvider";
import { t } from "@/lib/localization";

export default function SearchInput() {
    const {
        filesQuery,
        updateFilesQuery,
        updateFoldersQuery,
        parentFolder,
        changeParentFolder,
    } = useStorageQuery();

    const folderName = useMemo(() => parentFolder?.name, [parentFolder?.name]);
    const { badgeRef, badgeWidth } = useBadgeWidth(folderName);

    // controlled input value, synced to query.search (restored by history)
    const [value, setValue] = useState(filesQuery.search);

    // keep input in sync when query.search changes (e.g., after folder restore)
    useEffect(() => {
        setValue(filesQuery.search);
    }, [filesQuery.search]);

    // debounce updates to queries
    const debouncedUpdate = useMemo(
        () =>
            debounce((text: string) => {
                updateFilesQuery({ page: 1, search: text });
                updateFoldersQuery({ page: 1, search: text });
            }, 800),
        [updateFilesQuery, updateFoldersQuery]
    );

    useEffect(() => {
        return () => debouncedUpdate.cancel();
    }, [debouncedUpdate]);

    const onChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const text = e.target.value;
            setValue(text);
            debouncedUpdate(text);
        },
        [debouncedUpdate]
    );

    const cancelParentFolder = useCallback(() => {
        updateFilesQuery({
            page: 1,
        });
        updateFoldersQuery({
            page: 1,
        });
        changeParentFolder(null);
    }, [changeParentFolder, updateFilesQuery, updateFoldersQuery]);

    return (
        <div className="w-full sm:w-[320px] relative">
            <Input
                className="pe-8"
                style={
                    folderName
                        ? {
                              paddingInlineStart: badgeWidth + 12,
                          }
                        : {}
                }
                value={value}
                onChange={onChange}
                placeholder={t("common.search.placeholder")}
            />
            <Search className="size-3.5 absolute end-2.5 top-2.5" />
            {folderName && (
                <div
                    ref={badgeRef}
                    className="absolute start-1 top-1 max-w-[160px]"
                >
                    <Badge
                        variant="secondary"
                        className="h-7 flex items-center gap-1"
                    >
                        <button onClick={cancelParentFolder}>
                            <X className="size-3" />
                        </button>
                        <span className="truncate">{folderName}</span>
                    </Badge>
                </div>
            )}
        </div>
    );
}

const useBadgeWidth = (folderName?: string) => {
    const badgeRef = useRef<HTMLDivElement>(null);
    const [badgeWidth, setBadgeWidth] = useState(0);

    useEffect(() => {
        if (!badgeRef.current || !folderName) return;

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setBadgeWidth(entry.contentRect.width);
            }
        });

        observer.observe(badgeRef.current);

        return () => observer.disconnect();
    }, [folderName]);

    return { badgeRef, badgeWidth };
};
